// V2 application pre-qual letter tab: reads/writes /los/v2/application.
import React, { Component } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../App.css";
import * as com from "../../Common.js";
import { debounce } from "lodash";
import {
  Alert,
  Button,
  Col,
  Input,
  Modal,
  Row,
  Space,
  Typography,
  Upload,
} from "antd";
import { CloseOutlined, LockOutlined } from "@ant-design/icons";

import PreQualPlan from "./PreQualPlanPicker";
import {
  parseStringToNumber,
  USDollarCurrencyNoSignLocale,
} from "./utils";
import { getBest, getRearrangeProducts } from "./utils_loans";
import PreQualLetterHistory from "./PrequalLetters";
import { DollarPrefix } from "./DollarPrefix";
import { getErrorMessage } from "./error_codes";
import { PrequalLetterBrandingData } from "./utils_pdf";
import "./PrequalV2.css";
import {
  getLosApplicationV2,
  patchPropertyFieldsV2,
  patchSelectionV2,
} from "../../DocumentChecklist/applicationV2Client";
import {
  formatPrequalBorrowerEmail,
  formatPrequalBorrowerName,
  getPrimaryParticipant,
  loadPrequalQuotes,
} from "./prequalQuotes";

const { Paragraph, Text, Title } = Typography;

const DEFAULT_OPENING_TEXT =
  "Congratulations! Based on our review of the preliminary information you provided, we are pleased to confirm that you have been pre-qualified for a mortgage loan under the terms outlined below. This is an exciting first step toward your new home.";
const DEFAULT_COMPANY_TAGLINE = "Mortgage Lending";
const DEFAULT_LO_TITLE = "Loan Officer";
const clampPercentage = (value: number) =>
  Math.min(100, Math.max(0, value));

const resolveQuoteTerms = (application, local?: { purchasePrice: string; downpayment: number; loansize: number }) => {
  if (local) {
    const salesPrice = Math.max(0, parseStringToNumber(local.purchasePrice));
    return {
      salesPrice,
      downpayment: local.downpayment,
      loansize: local.loansize,
    };
  }
  const salesPrice = Math.max(0, com.safeParseInt(application?.property?.salesprice));
  let downpayment = com.safeParseInt(application?.selection?.downpayment);
  if (downpayment < 0 || downpayment > salesPrice) {
    downpayment = Math.min(Math.max(downpayment, 0), salesPrice);
  }
  const rawLoan = application?.selection?.loansize;
  const loansize =
    rawLoan === "" || rawLoan == null
      ? Math.max(0, salesPrice - downpayment)
      : com.safeParseInt(rawLoan);
  return { salesPrice, downpayment, loansize };
};

const normalizeLogoDataUrl = (logo?: string) => {
  if (!logo) {
    return "";
  }
  return logo.startsWith("data:") ? logo : `data:image/png;base64,${logo}`;
};

const getSvgTextFromDataUrl = (dataUrl: string) => {
  const [metadata, payload = ""] = dataUrl.split(",", 2);
  if (metadata.includes(";base64")) {
    return atob(payload);
  }
  return decodeURIComponent(payload);
};

type PrequalApplicationV2Props = {
  loanid: string;
  borrowerid: string;
  onApplicationChange?: (application: any) => void;
};

class PrequalApplicationV2 extends Component<
  PrequalApplicationV2Props,
  {
    loading: boolean;
    loadingPdf: boolean;
    purpose: string;
    loansize: number;
    downpayment: number;
    purchasePrice: string;
    closingCost: string;
    showPrequal: boolean;
    ltv: number;
    fico: number;
    bestProduct: any;
    application: any;
    displayingCreatePlanPanel: boolean;
    adjustmentTipVisible: boolean;
    activeCreateTab: "terms" | "branding";
    companyName: string;
    companyTagline: string;
    companyLogo: string;
    openingText: string;
    loName: string;
    loTitle: string;
    loNmls: string;
    loPhone: string;
    loEmail: string;
    companyNmls: string;
    companyAddress1: string;
    companyAddress2: string;
    editingDownPaymentPercent: boolean;
    logoError: string;
    errorCode?: string;
    errorText?: string;
  }
> {
  count: number;
  letterHistoryRef: React.RefObject<any>;
  planPickerRef: React.RefObject<any>;
  quoteSeq: number;
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      loadingPdf: false,
      purpose: "Purchase",
      loansize: 0,
      downpayment: 0,
      purchasePrice: "0",
      closingCost: "1000",
      showPrequal: false,
      ltv: 0,
      fico: 800,
      bestProduct: null,
      application: null,
      displayingCreatePlanPanel: false,
      adjustmentTipVisible: true,
      activeCreateTab: "terms",
      companyName: com.companyName || "Zeitro",
      companyTagline: DEFAULT_COMPANY_TAGLINE,
      companyLogo: normalizeLogoDataUrl(com.logo),
      openingText: DEFAULT_OPENING_TEXT,
      loName: "",
      loTitle: DEFAULT_LO_TITLE,
      loNmls: "",
      loPhone: "",
      loEmail: "",
      companyNmls: "",
      companyAddress1: "",
      companyAddress2: "",
      editingDownPaymentPercent: false,
      logoError: "",
    };
    this.count = 0;
    this.quoteSeq = 0;
    this.letterHistoryRef = React.createRef();
    this.planPickerRef = React.createRef();
  }
  componentDidMount() {
    this.fetchBrandingDefaults();
    this.setState({
      closingCost: USDollarCurrencyNoSignLocale.format(
        parseStringToNumber(this.state.closingCost)
      ),
    });
    this.loadApplication();
  }
  componentWillUnmount() {
    this.persistAndReload.flush();
    this.persistAndReload.cancel();
  }
  notifyApplicationChange = (application) => {
    const onApplicationChange = this.props.onApplicationChange;
    if (onApplicationChange) {
      onApplicationChange(application);
    }
  };
  applyApplication = (application) => {
    if (!application) return;
    this.notifyApplicationChange(application);
    const { salesPrice, downpayment, loansize } = resolveQuoteTerms(application);
    const person = getPrimaryParticipant(application);
    const fico = person?.estimatedFico > 0 ? person.estimatedFico : 800;
    const ltv = salesPrice > 0
      ? clampPercentage(Math.round((loansize / salesPrice) * 100))
      : 0;
    this.setState({
      application,
      purchasePrice: USDollarCurrencyNoSignLocale.format(salesPrice),
      downpayment,
      loansize,
      ltv,
      fico,
    });
  };
  loadApplication = async () => {
    if (!this.props.loanid) return;
    this.setState({ loading: true });
    try {
      const body = await getLosApplicationV2(this.props.loanid);
      this.applyApplication(body.Application);
      await this.reloadQuotes(body.Application);
    } catch (err) {
      console.error(err);
      this.setState({ loading: false });
    }
  };
  persistTerms = async () => {
    if (!this.props.loanid) return null;
    const salesPrice = Math.max(0, parseStringToNumber(this.state.purchasePrice));
    try {
      await patchPropertyFieldsV2(this.props.loanid, {
        salesprice: String(salesPrice),
        appraisal: String(salesPrice),
      });
      const body = await patchSelectionV2(this.props.loanid, {
        downpayment: String(this.state.downpayment),
        loansize: String(this.state.loansize),
      });
      if (body?.Application) {
        this.setState({ application: body.Application });
        this.notifyApplicationChange(body.Application);
      }
      return body?.Application || this.state.application;
    } catch (err) {
      console.error(err);
      return this.state.application;
    }
  };
  overlayQuoteApplication = (application) => {
    if (!application) return application;
    const { salesPrice, downpayment, loansize } = resolveQuoteTerms(
      application,
      this.state.application
        ? {
            purchasePrice: this.state.purchasePrice,
            downpayment: this.state.downpayment,
            loansize: this.state.loansize,
          }
        : undefined
    );
    return {
      ...application,
      property: {
        ...(application.property || {}),
        salesprice: String(salesPrice),
        appraisal: String(salesPrice),
      },
      selection: {
        ...(application.selection || {}),
        downpayment: String(downpayment),
        loansize: String(loansize),
      },
    };
  };
  reloadQuotes = async (applicationOverride = null) => {
    const seq = ++this.quoteSeq;
    this.setState({ loading: true, errorCode: null, bestProduct: null });
    try {
      const application = this.overlayQuoteApplication(
        applicationOverride || this.state.application
      );
      if (!application || !this.props.loanid) {
        this.setState({ loading: false });
        return;
      }
      const person = getPrimaryParticipant(application);
      const fico = this.state.application
        ? this.state.fico
        : person?.estimatedFico > 0
          ? person.estimatedFico
          : this.state.fico;
      const result = await loadPrequalQuotes({
        loanId: this.props.loanid,
        borrowerId: this.props.borrowerid,
        application,
        loansize: com.safeParseInt(application.selection?.loansize),
        fico,
      });
      if (seq !== this.quoteSeq) return;
      if (result.status !== "Ok") {
        this.setState({
          loading: false,
          showPrequal: false,
          bestProduct: null,
          errorCode: result["status code"],
          errorText: result.message,
        });
        return;
      }
      this.processProducts(result.result || []);
    } catch (err) {
      if (seq !== this.quoteSeq) return;
      console.error(err);
      this.setState({ loading: false, showPrequal: false, bestProduct: null });
    }
  };
  persistAndReload = debounce(async () => {
    const application = await this.persistTerms();
    await this.reloadQuotes(application);
  }, 400);
  refetchPreQualLetterHistory = () => {
    if (this.letterHistoryRef.current) {
      this.letterHistoryRef.current.fetchData();
    }
  };

  previewPrequalPDF = () => {
    if (this.planPickerRef.current) {
      this.planPickerRef.current.previewPrequalPDF();
    }
  };

  fetchBrandingDefaults = async () => {
    const token = sessionStorage.getItem("ZeitroA");
    if (!token) {
      return;
    }

    try {
      const [profileResponse, companyResponse, brandingResponse] =
        await Promise.all([
          fetch("/los/getprofile", {
            method: "GET",
            headers: {
              Authorization: "Bearer " + token,
              Cache: "no-cache",
            },
          }),
          fetch("/los/getcompanyprofilesetup", {
            method: "GET",
            headers: {
              Authorization: "Bearer " + token,
              Cache: "no-cache",
            },
          }),
          fetch("/los/getprequalbranding", {
            method: "GET",
            headers: {
              Authorization: "Bearer " + token,
              Cache: "no-cache",
            },
          }),
        ]);

      if (profileResponse.ok) {
        const profile = await profileResponse.json();
        const loanOfficer = profile?.LoanOfficers?.[0];
        if (loanOfficer) {
          this.setState({
            loName: `${loanOfficer.FirstName || ""} ${
              loanOfficer.LastName || ""
            }`.trim(),
            loNmls: loanOfficer.Nmls
              ? `NMLS #${loanOfficer.Nmls}`
              : this.state.loNmls,
            loPhone: loanOfficer.Phone || this.state.loPhone,
            loEmail: loanOfficer.Email || this.state.loEmail,
          });
        }
      }

      if (companyResponse.ok) {
        const company = await companyResponse.json();
        this.setState({
          companyName: company.Name || this.state.companyName,
          companyNmls: company.CompanyNMLS
            ? `Company NMLS #${company.CompanyNMLS}`
            : this.state.companyNmls,
          companyAddress1: company.Address || this.state.companyAddress1,
          companyAddress2: [company.City, company.State, company.Zip]
            .filter(Boolean)
            .join(", "),
        });
        if (company.Logo) {
          await this.setCompanyLogo(company.Logo);
        }
      }

      if (brandingResponse.ok) {
        const savedBranding = await brandingResponse.json();
        if (savedBranding.configured) {
          this.setState({
            companyName: savedBranding.companyName,
            companyTagline: savedBranding.companyTagline,
            openingText: savedBranding.openingText,
            loName: savedBranding.loName,
            loTitle: savedBranding.loTitle,
          });
          await this.setCompanyLogo(savedBranding.companyLogo);
        }
      }
    } catch (error) {
      console.error("Failed to load pre-qual branding defaults:", error);
    }
  };

  persistBrandingDefaults = async () => {
    const token = sessionStorage.getItem("ZeitroA");
    if (!token || this.getCompanyNameError()) {
      return;
    }

    try {
      const response = await fetch("/los/setprequalbranding", {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json",
          Cache: "no-cache",
        },
        body: JSON.stringify({
          companyName: this.state.companyName.trim(),
          companyTagline: this.state.companyTagline,
          companyLogo: this.state.companyLogo,
          openingText: this.state.openingText,
          loName: this.state.loName,
          loTitle: this.state.loTitle,
        }),
      });
      if (!response.ok) {
        throw new Error(`Branding defaults request failed: ${response.status}`);
      }
    } catch (error) {
      console.error("Failed to save pre-qual branding defaults:", error);
    }
  };

  convertSvgToPngDataUrl = (svgText: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const parser = new DOMParser();
      const svgDocument = parser.parseFromString(svgText, "image/svg+xml");
      const svgElement = svgDocument.documentElement;
      const viewBox = svgElement
        .getAttribute("viewBox")
        ?.split(/\s+/)
        .map(Number);
      const width =
        parseFloat(svgElement.getAttribute("width") || "") ||
        viewBox?.[2] ||
        420;
      const height =
        parseFloat(svgElement.getAttribute("height") || "") ||
        viewBox?.[3] ||
        120;
      const svgBlob = new Blob([svgText], {
        type: "image/svg+xml;charset=utf-8",
      });
      const svgUrl = URL.createObjectURL(svgBlob);
      const image = new Image();

      image.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = 2;
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));
        const context = canvas.getContext("2d");
        if (!context) {
          URL.revokeObjectURL(svgUrl);
          reject(new Error("Canvas is not supported"));
          return;
        }
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(svgUrl);
        resolve(canvas.toDataURL("image/png"));
      };

      image.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        reject(new Error("Failed to load SVG logo"));
      };

      image.src = svgUrl;
    });
  };

  handleLogoUpload = (file) => {
    const isSvg =
      file.type === "image/svg+xml" ||
      file.name?.toLowerCase().endsWith(".svg");
    const isSupportedImage =
      isSvg ||
      file.type === "image/png" ||
      file.type === "image/jpeg" ||
      /\.(png|jpe?g)$/i.test(file.name || "");

    if (!isSupportedImage) {
      this.setState({
        logoError: "Upload a PNG, JPG, or SVG image.",
      });
      return Upload.LIST_IGNORE;
    }

    if (file.size > 2 * 1024 * 1024) {
      this.setState({
        logoError: "Logo must be 2 MB or smaller.",
      });
      return Upload.LIST_IGNORE;
    }

    this.setState({ logoError: "" });
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        if (isSvg) {
          const companyLogo = await this.convertSvgToPngDataUrl(
            reader.result as string
          );
          this.setState({ companyLogo });
          return;
        }
        this.setState({ companyLogo: reader.result as string });
      } catch (error) {
        console.error("Failed to process pre-qual logo:", error);
        this.setState({ companyLogo: "" });
      }
    };
    if (isSvg) {
      reader.readAsText(file);
    } else {
      reader.readAsDataURL(file);
    }
    return false;
  };

  setCompanyLogo = async (logo?: string) => {
    const normalizedLogo = normalizeLogoDataUrl(logo);
    if (!normalizedLogo) {
      this.setState({ companyLogo: "" });
      return;
    }

    try {
      if (normalizedLogo.startsWith("data:image/svg+xml")) {
        const companyLogo = await this.convertSvgToPngDataUrl(
          getSvgTextFromDataUrl(normalizedLogo)
        );
        this.setState({ companyLogo });
        return;
      }
      this.setState({ companyLogo: normalizedLogo });
    } catch (error) {
      console.error("Failed to process pre-qual company logo:", error);
      this.setState({ companyLogo: "" });
    }
  };

  clearLogo = () => {
    this.setState({ companyLogo: "", logoError: "" });
  };

  updateDownPayment = (downPayment: number) => {
    const salesPrice = Math.max(
      0,
      parseStringToNumber(this.state.purchasePrice)
    );
    const boundedDownPayment = Math.min(
      Math.max(Math.round(downPayment), 0),
      salesPrice
    );
    const loanSize = salesPrice - boundedDownPayment;
    const newLTV =
      salesPrice > 0 ? Math.round((loanSize / salesPrice) * 100) : 0;

    this.setState({
      downpayment: boundedDownPayment,
      loansize: loanSize,
      ltv: newLTV,
      errorCode: null,
      bestProduct: null,
    }, () => this.persistAndReload());
  };

  updateDownPaymentPercent = (percentage: number) => {
    const salesPrice = Math.max(
      0,
      parseStringToNumber(this.state.purchasePrice)
    );
    const boundedPercentage = Math.min(
      Math.max(Math.round(percentage), 0),
      100
    );
    this.updateDownPayment((salesPrice * boundedPercentage) / 100);
  };

  getCompanyNameError = () => {
    const companyName = this.state.companyName.trim();
    if (!companyName) {
      return "Company name is required.";
    }
    if (companyName.length > 60) {
      return "Company name must be 60 characters or fewer.";
    }
    return "";
  };

  getFicoError = () => {
    if (this.state.fico < 300 || this.state.fico > 850) {
      return "FICO must be between 300 and 850.";
    }
    return "";
  };

  focusFirstValidationError = () => {
    if (this.getCompanyNameError()) {
      this.setState({ activeCreateTab: "branding" });
      return;
    }
    if (this.getFicoError()) {
      this.setState({ activeCreateTab: "terms" });
    }
  };

  getBrandingData = (): PrequalLetterBrandingData => {
    const logoMode: "text" | "logo" = this.state.companyLogo ? "logo" : "text";
    const branding = {
      logoMode,
      companyLogo: this.state.companyLogo,
      companyName: this.state.companyName,
      companyTagline: this.state.companyTagline,
      openingText: this.state.openingText,
      loName: this.state.loName,
      loTitle: this.state.loTitle,
      loNmls: this.state.loNmls,
      loPhone: this.state.loPhone,
      loEmail: this.state.loEmail,
      coName: this.state.companyName,
      coNmls: this.state.companyNmls,
      coAddr1: this.state.companyAddress1,
      coAddr2: this.state.companyAddress2,
    };

    return {
      ...branding,
      branding,
    };
  };

  askedForAssistance = () => false;

  cullRate = (products) => {
    if (products.length === 0) return products;

    let newproducts = [];
    newproducts.push(products[0]);
    let base_rate = products[0].base_rate;

    for (let i = 1; i < products.length; i++) {
      if (products[i].base_rate !== base_rate) {
        newproducts.push(products[i]);
        base_rate = products[i].base_rate;
      } else {
      }
    }
    return newproducts;
  };

  compareRate = (a, b) => {
    if (a.base_rate !== b.base_rate) return b.base_rate - a.base_rate;
    return b.closing_cost - a.closing_cost; // take into account remaining credits
  };

  processProducts = (products) => {
    console.log("products", products.length, products);
    this.setState({ loading: true });
    if (products.length > 0) {
      this.setState({ showPrequal: true });
    } else {
      this.setState({ loading: false, showPrequal: false, bestProduct: null });
      return;
    }

    let rearranged = getRearrangeProducts(
      products,
      this.state.application?.property?.propertytype
    );

    rearranged = rearranged.filter((product) => {
      return (
        Number(product.closing_cost) <=
        parseStringToNumber(this.state.closingCost)
      );
    });
    rearranged.sort(this.compareRate);
    rearranged = this.cullRate(rearranged);
    if (rearranged.length > 0) {
      this.setState({ showPrequal: true });
    } else {
      this.setState({ loading: false, showPrequal: false, bestProduct: null });
      return;
    }

    const bestProduct = getBest(rearranged);

    if (!!bestProduct) {
      this.setState({ showPrequal: true, bestProduct: bestProduct });
    } else {
      this.setState({ loading: false, showPrequal: false, bestProduct: null });
      return;
    }
    this.setState({ loading: false });
  };

  render = () => {
    const companyNameError = this.getCompanyNameError();
    const ficoError = this.getFicoError();
    const canGenerate = !companyNameError && !ficoError;

    return (
      <div className="mb-4  text-center text-wrap">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            marginBottom: "20px",
          }}
        >
          <Row style={{ padding: "20px" }}>
            <Space
              style={{
                width: "100%",
                justifyContent: "space-between",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Title level={4}>Pre-qualification letter</Title>
              <Button
                type="primary"
                onClick={() =>
                  this.setState({ displayingCreatePlanPanel: true })
                }
                style={{ height: "100%", padding: "0px 1em" }}
              >
                Create
              </Button>
            </Space>
          </Row>
          <PreQualLetterHistory
            ref={this.letterHistoryRef}
            name={formatPrequalBorrowerName(this.state.application)}
          />
          <Modal
            title={null}
            open={this.state.displayingCreatePlanPanel}
            footer={null}
            onCancel={() => this.setState({ displayingCreatePlanPanel: false })}
            width={940}
            zIndex={1000}
            centered
            className="prequal_create_modal"
            wrapClassName="prequal_create_modal_wrap"
          >
            <div className="prequal_create_modal_content text-left">
              <Title level={2} className="prequal_create_title">
                Create a pre-qualification letter
              </Title>
              <Paragraph className="prequal_create_description">
                Set the loan terms and customize the letter for{" "}
                {formatPrequalBorrowerName(this.state.application)}.
              </Paragraph>
              <div
                className={`prequal_plan_container ${
                  this.state.activeCreateTab === "branding"
                    ? "prequal_plan_container_branding"
                    : "prequal_plan_container_terms"
                }`}
              >
                <div className="prequal_create_tabbar">
                  <button
                    type="button"
                    className={
                      this.state.activeCreateTab === "terms"
                        ? "prequal_create_tab active"
                        : "prequal_create_tab"
                    }
                    onClick={() => this.setState({ activeCreateTab: "terms" })}
                  >
                    Loan terms
                  </button>
                  <button
                    type="button"
                    className={
                      this.state.activeCreateTab === "branding"
                        ? "prequal_create_tab active"
                        : "prequal_create_tab"
                    }
                    onClick={() =>
                      this.setState({ activeCreateTab: "branding" })
                    }
                  >
                    Branding & copy
                  </button>
                </div>
                <div className="prequal_content_body">
                  <div className="prequal_form_col">
                    {this.state.activeCreateTab === "terms" ? (
                      <div className="prequal_tab_panel">
                <Row className="prequal_plan_labels_row">
                    <span className="prequal_plan_labels">
                      Estimated purchase price
                    </span>
                  </Row>
                  <Row>
                    <Input
                      value={this.state.purchasePrice}
                      onChange={(e) => {
                        const salesPrice = parseStringToNumber(e.target.value);
                        const loanSize = Math.round(
                          (this.state.ltv * salesPrice) / 100
                        );
                        const downpayment = Math.ceil(salesPrice - loanSize);
                        this.setState({
                          downpayment,
                          loansize: loanSize,
                          purchasePrice:
                            USDollarCurrencyNoSignLocale.format(salesPrice),
                          errorCode: null,
                          bestProduct: null,
                        }, () => this.persistAndReload());
                      }}
                      prefix={<DollarPrefix />}
                      type="text"
                      className="prequal_plan_controls"
                    />
                  </Row>
                  <Row className="prequal_plan_labels_row">
                    <span className="prequal_plan_labels">Down payment</span>
                  </Row>
                  <Row gutter={16}>
                    <Col span={18}>
                    <Input
                      value={USDollarCurrencyNoSignLocale.format(
                        this.state.downpayment
                      )}
                      onChange={(e) => {
                        this.updateDownPayment(
                          parseStringToNumber(e.target.value)
                        );
                      }}
                      prefix={<DollarPrefix />}
                      type="text"
                      className="prequal_plan_controls"
                    />
                    </Col>
                    <Col span={6}>
                      {this.state.editingDownPaymentPercent ? (
                        <Input
                          autoFocus
                          min={0}
                          max={100}
                          type="number"
                          value={100 - this.state.ltv}
                          suffix="%"
                          className="prequal_down_payment_percent_input"
                          onChange={(e) =>
                            this.updateDownPaymentPercent(
                              com.safeParseInt(e.target.value)
                            )
                          }
                          onBlur={() =>
                            this.setState({ editingDownPaymentPercent: false })
                          }
                          onPressEnter={(e) => e.currentTarget.blur()}
                        />
                      ) : (
                        <button
                          type="button"
                          className="prequal_down_payment_percent"
                          onClick={() =>
                            this.setState({ editingDownPaymentPercent: true })
                          }
                        >
                          {100 - this.state.ltv}%
                        </button>
                      )}
                    </Col>
                  </Row>

                  <Row className="prequal_plan_labels_row">
                    <span className="prequal_plan_labels">
                      Maximum closing cost
                    </span>
                  </Row>
                  <Row>
                    <Input
                      value={this.state.closingCost}
                      onChange={(e) => {
                        const closingCost = parseStringToNumber(e.target.value);
                        this.setState({
                          closingCost: USDollarCurrencyNoSignLocale.format(
                            Number(closingCost)
                          ),
                          errorCode: null,
                          bestProduct: null,
                        }, () => this.reloadQuotes());
                      }}
                      prefix={<DollarPrefix />}
                      className="prequal_plan_controls"
                      type="text"
                    />
                  </Row>
                  <Row gutter={16} className="prequal_plan_labels_row">
                    <Col span={12}>
                      <span className="prequal_plan_labels">Loan-to-value (LTV)</span>
                    </Col>
                    <Col span={12}>
                      <span className="prequal_plan_labels">FICO</span>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Input
                        value={this.state.ltv}
                        readOnly
                        aria-readonly="true"
                        className="prequal_plan_controls prequal_readonly_control"
                        suffix="%"
                      />
                    </Col>
                    <Col span={12}>
                      <Input
                        value={this.state.fico}
                        min={300}
                        max={850}
                        onChange={(e) => {
                          const newFICO = com.safeParseInt(e.target.value);
                          this.setState({
                            fico: newFICO,
                            errorCode: null,
                            bestProduct: null,
                          }, () => {
                            if (this.state.fico >= 300 && this.state.fico <= 850) {
                              this.reloadQuotes();
                            }
                          });
                        }}
                        className="prequal_plan_controls"
                        status={ficoError ? "error" : ""}
                        type="number"
                        suffix=""
                      />
                      {ficoError && (
                        <Text className="prequal_field_error">{ficoError}</Text>
                      )}
                    </Col>
                  </Row>
                      </div>
                    ) : (
                      <div className="prequal_tab_panel">
                            <Row className="prequal_plan_labels_row">
                              <span className="prequal_plan_labels">
                                Company name
                              </span>
                            </Row>
                            <Row>
                              <Input
                                value={this.state.companyName}
                                onChange={(e) =>
                                  this.setState({
                                    companyName: e.target.value,
                                  })
                                }
                                placeholder="Company name"
                                maxLength={60}
                                status={companyNameError ? "error" : ""}
                                className="prequal_plan_controls"
                              />
                            </Row>
                            {companyNameError && (
                              <Text className="prequal_field_error">
                                {companyNameError}
                              </Text>
                            )}
                            <Row className="prequal_stacked_input_row">
                              <Input
                                value={this.state.companyTagline}
                                onChange={(e) =>
                                  this.setState({
                                    companyTagline: e.target.value,
                                  })
                                }
                                placeholder="Tagline (optional)"
                                className="prequal_plan_controls"
                              />
                            </Row>
                            <Row className="prequal_plan_labels_row">
                              <span className="prequal_plan_labels">
                                Company logo{" "}
                                <span className="prequal_optional_label">
                                  · optional
                                </span>
                              </span>
                            </Row>
                            <Row>
                              <Upload
                                accept="image/png,image/jpeg,image/svg+xml"
                                showUploadList={false}
                                beforeUpload={this.handleLogoUpload}
                                className="prequal_logo_upload"
                              >
                                <div className="prequal_logo_dropzone">
                                  {this.state.companyLogo ? (
                                    <>
                                      <img
                                        src={this.state.companyLogo}
                                        alt="Company logo"
                                        className="prequal_logo_preview"
                                      />
                                      <Button
                                        aria-label="Remove company logo"
                                        className="prequal_logo_remove"
                                        icon={<CloseOutlined rev={undefined} />}
                                        onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          this.clearLogo();
                                        }}
                                      />
                                    </>
                                  ) : (
                                  <span>Drop logo - PNG, JPG, or SVG</span>
                                  )}
                                </div>
                              </Upload>
                            </Row>
                            <Text className="prequal_logo_hint">
                              Shown top-left of the letter in place of the
                              company name. Wide or square logos both fit -
                              leave empty to use the name above.
                            </Text>
                            {this.state.logoError && (
                              <Text className="prequal_field_error">
                                {this.state.logoError}
                              </Text>
                            )}
                            <Row className="prequal_plan_labels_row">
                              <span className="prequal_plan_labels">
                                Opening message
                              </span>
                            </Row>
                            <Row>
                              <Input.TextArea
                                value={this.state.openingText}
                                onChange={(e) =>
                                  this.setState({
                                    openingText: e.target.value.slice(0, 500),
                                  })
                                }
                                maxLength={500}
                                showCount
                                rows={4}
                                className="prequal_plan_controls prequal_copy_textarea"
                              />
                            </Row>
                            <Row className="prequal_plan_labels_row">
                              <span className="prequal_plan_labels">
                                Loan officer signature
                              </span>
                            </Row>
                            <Row gutter={16}>
                              <Col span={12}>
                                <Input
                                  value={this.state.loName}
                                  onChange={(e) =>
                                    this.setState({ loName: e.target.value })
                                  }
                                  placeholder="Full name"
                                  className="prequal_plan_controls"
                                />
                              </Col>
                              <Col span={12}>
                                <Input
                                  value={this.state.loTitle}
                                  onChange={(e) =>
                                    this.setState({ loTitle: e.target.value })
                                  }
                                  placeholder="Title"
                                  className="prequal_plan_controls"
                                />
                              </Col>
                            </Row>
                            <Row gutter={16} style={{ marginTop: "12px" }}>
                              <Col span={12}>
                                <Input
                                  value={this.state.loNmls}
                                  readOnly
                                  aria-readonly="true"
                                  placeholder="NMLS #"
                                  suffix={
                                    <LockOutlined
                                      aria-hidden="true"
                                      rev={undefined}
                                    />
                                  }
                                  className="prequal_plan_controls prequal_signature_readonly_control"
                                />
                              </Col>
                              <Col span={12}>
                                <Input
                                  value={this.state.loPhone}
                                  readOnly
                                  aria-readonly="true"
                                  placeholder="Phone"
                                  suffix={
                                    <LockOutlined
                                      aria-hidden="true"
                                      rev={undefined}
                                    />
                                  }
                                  className="prequal_plan_controls prequal_signature_readonly_control"
                                />
                              </Col>
                            </Row>
                            <Row style={{ marginTop: "12px" }}>
                              <Input
                                value={this.state.loEmail}
                                readOnly
                                aria-readonly="true"
                                placeholder="Email"
                                suffix={
                                  <LockOutlined
                                    aria-hidden="true"
                                    rev={undefined}
                                  />
                                }
                                className="prequal_plan_controls prequal_signature_readonly_control"
                              />
                            </Row>
                      </div>
                    )}
                  </div>
                  <div className="prequal_summary_col">
                  <PreQualPlan
                    ref={this.planPickerRef}
                    borrowerID={this.props.borrowerid}
                    loanID={this.props.loanid}
                    ltv={this.state.ltv}
                    purchasePrice={parseStringToNumber(this.state.purchasePrice)}
                    bestProduct={this.state.bestProduct}
                    email={formatPrequalBorrowerEmail(this.state.application)}
                    borrowerName={formatPrequalBorrowerName(this.state.application)}
                    downpayment={this.state.downpayment}
                    brandingData={this.getBrandingData()}
                    canGenerate={canGenerate}
                    onValidationError={this.focusFirstValidationError}
                    onPersistBranding={this.persistBrandingDefaults}
                    onClickSendToBorrower={() => {
                      this.setState({ displayingCreatePlanPanel: false });
                      this.refetchPreQualLetterHistory();
                    }}
                    loading={this.state.loading}
                  />
                  </div>
                </div>
              </div>

              {/* This is kept for debugging to check if our products logic is working or not. 
              We should remove it after we are sure that the products logic is working. */}
              {(!(this.state.showPrequal || this.state.loading) ||
                this.state.errorCode) && (
                <Alert
                  style={{ marginTop: "20px", display: "none" }}
                  message={getErrorMessage(
                    this.state.errorCode,
                    this.state.errorText
                  )}
                  type="error"
                />
              )}
            </div>
          </Modal>
        </div>
      </div>
    );
  };
}

export default PrequalApplicationV2;
